import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';

import { fetchDecks as fetchDecksService, fetchDeckAssets, fetchCollection as fetchCollectionService, fetchFullCollection as fetchFullCollectionService, fetchWallet as fetchWalletService, fetchShopItems as fetchShopItemsService } from './apiService';

// 1. Create the Context
const ApiContext = createContext(null);

// same rule as the shop's FREE price label, minus items the player can't claim anymore
const isFreeShopItem = (item) => item.canClaim && (item.price === 0 || item.nextClaimFree);

// 2. Create the Provider Component
export const ApiProvider = ({ children }) => {
  const [decks, setDecks] = useState([]);
  const [isLoadingDecks, setIsLoadingDecks] = useState(false);
  const [decksError, setDecksError] = useState(null);
  const [selectedDeck, setSelectedDeck] = useState(null);

  const [collection, setCollection] = useState(null);
  const [isLoadingCollection, setIsLoadingCollection] = useState(false);
  const [collectionError, setCollectionError] = useState(null);

  const [fullCollection, setFullCollection] = useState(null);
  const [isLoadingFullCollection, setIsLoadingFullCollection] = useState(false);
  const [fullCollectionError, setFullCollectionError] = useState(null);

  const [coins, setCoins] = useState(null);
  const [isLoadingCoins, setIsLoadingCoins] = useState(false);
  const [coinsError, setCoinsError] = useState(null);

  const [shopItems, setShopItems] = useState(null);

  // Function to fetch decks
  const fetchDecks = useCallback(async () => {
    setIsLoadingDecks(true);
    setDecksError(null);
    try {
      const data = await fetchDecksService();
      setDecks(data);
      const selectedDeckId = localStorage.getItem('selectedDeck');
      let deck = data.find(deck => deck.id === selectedDeckId);
      if (!deck && data.length > 0) {
        deck = data[0];
        localStorage.setItem('selectedDeck', deck.id);
      }
      if (!deck) {
        localStorage.removeItem('selectedDeck');
      }
      setSelectedDeck(deck || null);
      return data;
    } catch (error) {
      console.error("Error fetching decks in provider:", error);
      setDecksError(error.message || 'Failed to fetch decks');
    } finally {
      setIsLoadingDecks(false);
    }
  }, []); // Empty dependency array means this function is stable

  const fetchCollection = useCallback(async () => {
    setIsLoadingCollection(true);
    setCollectionError(null);
    try {
      const data = await fetchCollectionService();
      setCollection(data);
    } catch (error) {
      console.error("Error fetching collection in provider:", error);
      setCollectionError(error.message || 'Failed to fetch collection');
    } finally {
      setIsLoadingCollection(false);
    }
  }, []);

  const fetchFullCollection = useCallback(async () => {
    setIsLoadingFullCollection(true);
    setFullCollectionError(null);
    try {
      const data = await fetchFullCollectionService();
      setFullCollection(data);
    } catch (error) {
      console.error("Error fetching full collection in provider:", error);
      setFullCollectionError(error.message || 'Failed to fetch full collection');
    } finally {
      setIsLoadingFullCollection(false);
    }
  }, []);

  const fetchWallet = useCallback(async () => {
    setIsLoadingCoins(true);
    setCoinsError(null);
    try {
      const data = await fetchWalletService();
      setCoins(data?.coins ?? 0);
    } catch (error) {
      console.error("Error fetching wallet in provider:", error);
      setCoinsError(error.message || 'Failed to fetch wallet');
    } finally {
      setIsLoadingCoins(false);
    }
  }, []);

  const fetchShopItems = useCallback(async () => {
    try {
      const data = await fetchShopItemsService();
      setShopItems(data);
      return data;
    } catch (error) {
      console.error("Error fetching shop items in provider:", error);
    }
  }, []);

  // Fetch decks on initial mount
  useEffect(() => {
    fetchDecks();
    fetchCollection();
    fetchFullCollection();
    fetchWallet();
    fetchShopItems();
  }, [fetchDecks, fetchFullCollection]);

  // setSelectedDeck: updates both state and localStorage
  const setSelectedDeckAndPersist = useCallback((deck) => {
    setSelectedDeck(deck);
    if (deck && deck.id) {
      localStorage.setItem('selectedDeck', deck.id);
    } else {
      localStorage.removeItem('selectedDeck');
    }
  }, []);

  // Value provided by the context
  const value = {
    decks,
    isLoadingDecks,
    decksError,
    fetchDecks,
    fetchDeckAssets,
    selectedDeck,
    setSelectedDeck: setSelectedDeckAndPersist,
    collection,
    isLoadingCollection,
    collectionError,
    fetchCollection,
    fullCollection,
    isLoadingFullCollection,
    fullCollectionError,
    fetchFullCollection,
    coins,
    isLoadingCoins,
    coinsError,
    fetchWallet,
    setCoins,
    shopItems,
    fetchShopItems,
    setShopItems,
    hasFreeShopItem: (shopItems || []).some(isFreeShopItem),
  };

  return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>;
};

// 3. Create a Custom Hook for easy consumption
export const useApi = () => {
  const context = useContext(ApiContext);
  if (context === null) {
    throw new Error('useApi must be used within an ApiProvider');
  }
  return context;
}; 